import { useEffect, useMemo, useRef, useState } from "react";
import { MESSAGE_SPACER_GAP } from "../../AiToolboxSection.constants.ts";
import type {
  Messages,
  StreamingContent,
  UseAiAssistantMessagesScrollReturn,
} from "./AiAssistantMessages.types";

const SCROLL_NEAR_BOTTOM_PX = 80;

export function useAiAssistantMessagesScroll(
  messages: Messages,
  streamingContent: StreamingContent
): UseAiAssistantMessagesScrollReturn {
  const [spacerHeight, setSpacerHeight] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastUserMsgRef = useRef<HTMLDivElement>(null);
  const prevRef = useRef({ length: 0, firstId: "" });
  const hasMountedRef = useRef(false);

  useEffect(() => {
    const isMount = !hasMountedRef.current;
    hasMountedRef.current = true;

    const prev = prevRef.current;
    const len = messages.length;
    const firstId = messages[0]?.id ?? "";
    const lastType = messages[len - 1]?.type;

    // Messages already present on mount are a hydrate (story fixture or history).
    // Smooth-scroll + spacer there overflows by a few pixels and visual shots
    // land on either scroll position. A real send is a later length increase.
    const isNewHumanMsg =
      !isMount &&
      len > prev.length &&
      lastType === "human" &&
      (prev.length === 0 || firstId === prev.firstId);

    const isSessionSwitch =
      len > 0 && firstId !== prev.firstId && prev.firstId !== "";

    const isNewSession = prev.length > 0 && len === 0;

    const isInitialHydrate = prev.length === 0 && len > 0 && !isNewHumanMsg;

    prevRef.current = {
      length: len,
      firstId,
    };

    if (isNewHumanMsg) {
      requestAnimationFrame(() => {
        const containerH = scrollRef.current?.clientHeight ?? 0;
        const userMsgH = lastUserMsgRef.current?.offsetHeight ?? 0;
        setSpacerHeight(Math.max(0, containerH - userMsgH - MESSAGE_SPACER_GAP));
        scrollRef.current?.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: "smooth",
        });
      });
    } else if (isSessionSwitch || isNewSession || isInitialHydrate) {
      setSpacerHeight(0);
      requestAnimationFrame(() => {
        const el = scrollRef.current;
        if (el) el.scrollTo({ top: el.scrollHeight });
      });
    }
  }, [messages]);

  useEffect(() => {
    if (!streamingContent && spacerHeight > 0) {
      setSpacerHeight(0);
    }
  }, [streamingContent, spacerHeight]);

  useEffect(() => {
    if (!streamingContent) return;
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom <= SCROLL_NEAR_BOTTOM_PX) {
      requestAnimationFrame(() => {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      });
    }
  }, [streamingContent]);

  return { scrollRef, lastUserMsgRef, spacerHeight };
}

export const useAiAssistantMessages = (messages: Messages) => {
  return useMemo(() => {
    const n = messages.length;
    const lastHumanIdx = messages.findLastIndex((m) => m.type === "human");

    const hasFollowingAiAfter: boolean[] = new Array(n).fill(false);

    for (let i = n - 2; i >= 0; i--) {
      hasFollowingAiAfter[i] =
        messages[i + 1]!.type === "ai" || hasFollowingAiAfter[i + 1]!;
    }

    let previousPromptContext = messages.find((m) => m.type === "human")?.context;

    const contextChanged = messages.map((msg, idx) => {
      if (msg.type !== "human") return false;
      const changed = idx === 0 || previousPromptContext !== msg.context;
      if (idx > 0) {
        previousPromptContext = msg.context;
      }
      return changed;
    });

    return { lastHumanIdx, hasFollowingAiAfter, contextChanged };
  }, [messages]);
};
